import { useCallback, useEffect, useReducer } from "react";
import { useQuery } from "@tanstack/react-query";

import { GET, type DocWorkflowData, type DocWorkflowStepQuestionDatum } from "~utils";

export type TaskOnChange = (attribute: string, answerValue?: string) => void;

export type TaskAction =
  | { type: "LOAD_TASK"; payload: DocWorkflowData }
  | {
      type: "UPDATE_ATTRIBUTE";
      payload: Partial<DocWorkflowStepQuestionDatum> &
        Pick<DocWorkflowStepQuestionDatum, "attribute">;
    };

function taskReducer(
  state: DocWorkflowData | undefined,
  action: TaskAction,
): DocWorkflowData | undefined {
  switch (action.type) {
    case "LOAD_TASK":
      return action.payload;
    case "UPDATE_ATTRIBUTE": {
      if (!state) return state;
      const { attribute, ...patch } = action.payload;
      return {
        ...state,
        stepQuestionsData: state.stepQuestionsData.map((row) =>
          row.attribute === attribute ? { ...row, ...patch, attribute } : row,
        ),
      };
    }
    default:
      return state;
  }
}

type UseTaskParams = {
  task?: string;
  step?: string;
};

export function useTask(params: UseTaskParams) {
  const { task, step } = params;

  const {
    data: queryTaskData,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["sub", "documents", "workflow", task, step],
    queryFn: () => {
      if (!task) {
        throw new Error("useTask: query ran without task");
      }
      return GET("/api/sub/documents/workflow", {
        queryParams: {
          "task-id": task,
          ...(step ? { "step-id": step } : {}),
        },
      });
    },
    select: (response) => response.data,
    enabled: Boolean(task),
  });

  const [reducerTaskData, dispatch] = useReducer(taskReducer, undefined);

  useEffect(() => {
    if (queryTaskData) {
      dispatch({ type: "LOAD_TASK", payload: queryTaskData });
    }
  }, [task, step, queryTaskData]);

  const taskData = reducerTaskData ?? queryTaskData;

  const onChange = useCallback<TaskOnChange>(
    (attribute, answerValue) => {
      dispatch({ type: "UPDATE_ATTRIBUTE", payload: { attribute, answerValue } });
    },
    [dispatch],
  );

  return {
    taskData,
    dispatch,
    onChange,
    isPending,
    isError,
  };
}
